import { MutableRefObject, useEffect, useState } from 'react'
import ReactFC from 'react-fusioncharts'
import { formatPrice, getToFixed2Number, User } from 'utilities'
import { Series } from '../MSLine'
import { CategoryToolTipProps } from '../MSLine/CategoryToolTip'
import { ToolTipContentProps } from '../MSLine/ToolTipContent'
import styles from './DualAxis.module.scss'

const label = User.isShopOwner() ? 'expenses' : 'revenue'

export const dataSource = {
  chart: {
    baseFont: 'Open Sans',
    baseFontSize: '12',
    baseFontColor: styles.primaryContent,
    yAxisName: 'Hours',
    yAxisNameFontColor: styles.placeholderContent,
    yAxisNameFontBold: '0',
    numberPrefix: undefined,
    syAxisName: 'USD',
    syAxisNameFontColor: styles.placeholderContent,
    syAxisNameFontBold: '0',
    xAxisName: 'Period',
    xAxisNameFontColor: styles.placeholderContent,
    xAxisNameFontBold: '0',
    // labelDisplay: 'auto',
    // scrollHeight: '20',
    // numVisiblePlot: '20',
    // drawCrossLine: '1',
    theme: 'fusion',
    legendPosition: 'top-left',
    showBorder: '0',
    showPlotBorder: '0',
    showLegendBorder: '0',
    bgAlpha: 1,
    showValues: '0',
    showAlternateHGridColor: '0',
    divLineAlpha: '10',
    legendBorderThickness: '0',
    legendBgAlpha: 0,
    canvasBorderThickness: '0',
    legendShadow: '0',
    canvasBgAlpha: 0.1,
    showLegend: 1,
    numDivLines: 8,
    anchorRadius: 5,
    anchorBgAlpha: 100,
    paletteColors: '#3AABF2, #254291, #70D6C6, #2985AE, #B021D9, #2D65FF',
    toolTipBorderRadius: '2',
    toolTipBorderThickness: '0',
    toolTipBgColor: '#11174B',
    toolTipBgAlpha: '90',
    toolTipGrayOutColor: styles.secondaryContent,
    toolTipColor: styles.white,
    // toolTipFontFamily: 'Open Sans',
    // toolTipFontSize: '14',
    // toolTipFontWeight: 'bold',
    showToolTipShadow: '0',
    plotHoverEffect: '1',
    usePlotGradientColor: '0',
    // plotFillHoverColor: '#00ffaa',
    // plotBorderHoverThickness: '1',
    // plotBorderHoverDashed: '1',
    // plotBorderHoverDashLen: '6',
    // plotBorderHoverDashGap: '2',
    // drawCrossLine: '1',
    // plotToolText: 'Test plot tool text'

    plotToolText: `<div id='divToolTip' class=${styles.toolTipContainer} />`
  },
  categories: [
    {
      category: [
        {
          label: 'Mon'
        },
        {
          label: 'Tue'
        },
        {
          label: 'Wed'
        },
        {
          vline: 'true',
          lineposition: '0',
          color: '#6baa01',
          labelHAlign: 'center',
          labelPosition: '0',
          label: 'National holiday',
          dashed: '1'
        },
        {
          label: 'Thu'
        },
        {
          label: 'Fri'
        },
        {
          label: 'Sat'
        },
        {
          label: 'Sun'
        }
      ]
    }
  ],
  dataset: [
    {
      seriesname: 'Add ' + label,
      // plottooltext: 'Add ' + label + ': $dataValue $',
      color: '#70D6C6',
      renderAs: 'bar',
      parentYAxis: 'S',
      data: []
    },
    {
      seriesname: 'Purchase revenue',
      // plottooltext: 'Purchase revenue: $dataValue',
      color: '#254291',
      renderAs: 'bar',
      parentYAxis: 'S',
      data: []
    },
    {
      seriesname: 'Total watch time',
      renderAs: 'area',
      alpha: '30',
      parentYAxis: 'Y',
      showAnchors: '0',
      anchorBgColor: '#3AABF2',
      color: '#3AABF2',
      // plottooltext: 'Total watch time: $dataValue hours',
      data: []
    },
    {
      seriesname: 'Total video interaction time',
      parentYAxis: 'Y',
      renderAs: 'line',
      showValues: '0',
      anchorBgColor: '#457570',
      color: '#457570',
      data: []
    },
    {
      seriesname: 'Total page interaction time',
      parentYAxis: 'Y',
      renderAs: 'line',
      // plottooltext: 'Total interaction time: $dataValue hours',
      showValues: '0',
      anchorBgColor: '#FFFFFF',
      color: '#3AABF2',
      data: []
    },
    {
      seriesname: 'Total VOV interaction time',
      parentYAxis: 'Y',
      renderAs: 'line',
      showValues: '0',
      anchorBgColor: '#FFFFFF',
      color: '#38A19D',
      data: []
    },
    {
      seriesname: 'Total VOV watch time',
      parentYAxis: 'Y',
      renderAs: 'line',
      showValues: '0',
      anchorBgColor: '#FFFFFF',
      color: '#5E38A1',
      data: []
    },
  ]
}

export const useCategoryToolTipProps = (
  chartContainer: MutableRefObject<ReactFC>,
  dataSource: any
): { categoryToolTipProps: CategoryToolTipProps } => {
  const [
    categoryToolTipProps,
    setCategoryToolTipProps
  ] = useState<CategoryToolTipProps>(null)

  useEffect(() => {
    const applyEffect = () => {
      if (!chartContainer.current) return

      const container = (chartContainer.current as any).chartObj.container
      const textTagsAll: HTMLElement[] = Array.from(container.querySelectorAll('text'))

      const categoryLabels: string[] = dataSource?.categories?.[0].category?.map(category => category.label) || []

      let lowestYAttribute = null

      const categoryLabelTextTags: HTMLElement[] = textTagsAll
        .filter((textTag) => {
          const attrY = Math.round(textTag.getAttribute('y') as any)
          lowestYAttribute = lowestYAttribute
            ? Math.min(lowestYAttribute, attrY)
            : attrY

          return categoryLabels.includes(textTag.textContent)
        })

      const graphContainerHeight = Math.round(categoryLabelTextTags[0]?.getAttribute('y') as any) - lowestYAttribute

      categoryLabelTextTags.forEach(textTag => {
        textTag.addEventListener('mouseenter', (ev: MouseEvent) => {
          textTag.style.fontWeight = 'bold'
          if (textTag.children && textTag.children.length) {
            Array.from(textTag.children).forEach((child: HTMLElement) => {
              child.style.fontWeight = 'bold'
            })
          }

          const toolTipContentProps: ToolTipContentProps = {
            header: {
              label: `${dataSource.chart.yAxisName} / ${dataSource.chart.syAxisName}`,
              value: textTag.textContent
            },
            list: dataSource.dataset
              .filter((series: Series) => !series.isBest)
              .map((series: Series) => {
                let value: string | number = getToFixed2Number(
                  series.data?.[categoryLabels.findIndex(label => label === textTag.textContent)]?.value
                )
                value = series.seriesname.includes('revenue')
                  ? formatPrice(value)
                  : `${value} hrs`
                return {
                  label: series.seriesname,
                  color: series.anchorBgColor || series.color,
                  image: series.image,
                  value
                }
              })
          }
          const left = Math.round((ev as any).target.getAttribute('x')) + (ev.clientX - ev.offsetX)
          const top = Math.round((ev as any).target.getAttribute('y')) + (ev.clientY - ev.offsetY) - graphContainerHeight

          setCategoryToolTipProps({
            style: {
              height: graphContainerHeight,
              top,
              left
            },
            toolTipContentProps
          })
        }, false)

        // onMouseLeave
        textTag.addEventListener('mouseleave', (_: MouseEvent) => {
          textTag.style.fontWeight = 'normal'

          if (textTag.childElementCount) {
            Array.from(textTag.children).forEach((child: HTMLElement) => {
              child.style.fontWeight = 'normal'
            })
          }

          setCategoryToolTipProps(null)
        }, false)
      })
    }

    const runAction = () => {
      try {
        applyEffect()
      } catch (err) {
        // console.error(err)
        setTimeout(runAction, 1000)
      }
    }
    setTimeout(runAction, 1000)
  }, [chartContainer, dataSource, dataSource.dataset])

  return { categoryToolTipProps }
}
